import {
  createTable,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
} from '@tanstack/table-core';
import {
  Virtualizer,
  observeElementRect,
  observeElementOffset,
  elementScroll,
} from '@tanstack/virtual-core';
import Store from '$$store/main';
import reportHelpers from '../helpers';

const TABLE_STYLES = {
  ROWS: {
    sideBorders: {
      borderLeft: '1px solid #E5E5E5',
      borderRight: '1px solid #E5E5E5',
    },
    borderTop: { borderTop: '1px solid #E5E5E5' },
    borderBottom: { borderBottom: '1px solid #E5E5E5' },
    topRadius: { borderRadius: '8px 8px 0 0' },
    bottomRadius: { borderRadius: '0 0 8px 8px' },
    allBorderRadius: { borderRadius: '8px' },
    marginTop: { marginTop: '16px' },
    backgroundColor: {
      0: '#fff',
      1: '#F2F2F2',
      2: '#F8F8F8',
      3: '#FCFCFC',
      4: '#FCFCFC',
    },
    paddingTop: { paddingTop: '16px'},
  },
};
export default class ReportTable {
  constructor() {
    this.isUpdating = false;
    this.tableInstance = null;
    this.virtualizerInstance = null;
    this.tableHeaders = [];
    this.tableRows = [];
    this.virtualRows = [];
    this.virtualizerTotalSize = 0;
    this.columnMinSize = 75;
    this.controllerColumnSizing = {};
  }

  initTable(table) {
    const {
      data, state, updateColumnSizingVue, storageId,
    } = table;
    const { columns, columnSizing } = this.initVisibleColumnsAndSizes(data.columns, state, storageId);

    this.controllerColumnSizing = columnSizing;
    this.tableInstance = createTable({
      data: data.data,
      columns,
      state: {
        ...state,
        columnSizing: columnSizing,
        columnSizingInfo: {},
      },
      getCoreRowModel: getCoreRowModel(),
      getGroupedRowModel: getGroupedRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: updaterOrValue => this.setSorting(updaterOrValue),
      onExpandedChange: updaterOrValue => this.setExpanded(updaterOrValue),

      columnResizeMode: 'onChange',
      onColumnSizingInfoChange: updaterOrValue => {
        const info = typeof updaterOrValue === 'function'
          ? updaterOrValue(this.tableInstance.getState().columnSizingInfo)
          : updaterOrValue;

        this.tableInstance?.setOptions(prev => ({
          ...prev,
          state: {
            ...prev.state,
            columnSizingInfo: info,
          },
        }));

        this.onResize(info);
        updateColumnSizingVue();
      },
    });
  }

  initVirtualizer(scrollerElement, updaterCallback) {
    const count = this.tableInstance?.getExpandedRowModel().rows.length;

    this.virtualizerInstance = new Virtualizer({
      count,
      getScrollElement: () => scrollerElement,
      estimateSize: index => {
        const rows = this.tableInstance?.getExpandedRowModel().rows || [];
        const row = rows[index];

        if (!row) {
          return 36;
        }

        const grouping = this.tableInstance?.getState().grouping;
        const isTopLevelGroup = row.depth === 0;

        if (!grouping?.length || !isTopLevelGroup) {
          return 36;
        }

        const previousTopLevelRows = rows.slice(0, index).filter(r => r.depth === 0);
        const prevGroupsCount = previousTopLevelRows.length;

        if (prevGroupsCount === 0) {
          return 36;
        }

        return 36 + 16;
      },
      overscan: 5,
      observeElementRect,
      observeElementOffset,
      scrollToFn: elementScroll,
      onChange: instance => {
        let virtualizerTotalSize = instance.getTotalSize();

        this.virtualizerTotalSize = virtualizerTotalSize + 36;
        this.updateTable();
        updaterCallback();
      },
    });

    // Manual update if needed: this.virtualizerInstance.options.onChange(this.virtualizerInstance)

    this.virtualizerInstance._willUpdate(); // Don't remove!!!
    this.updateTable();
    updaterCallback();
  }

  initVisibleColumnsAndSizes(columns, state, storageId) {
    const leftSidebarWidth = Store.getters.leftSidebarWidth;
    const storageColumnSizing = JSON.parse(localStorage.getItem(storageId));

    const visibleColumns = this.getOrderedVisibleColumns(columns, state)
    const columnsCountWithAutoWidth = visibleColumns.filter(col => col.size === 'auto');
    const columnsSum = visibleColumns.filter(col => col.size && col.size !== 'auto').reduce((sum, col) => sum + col.size, 0);

    if (storageColumnSizing && Object.keys(storageColumnSizing).length) {
      const columnSizing = { ...storageColumnSizing };
      const turnedOff = [];
      const turnedOn = [];

      for (const colId of columns.map(col => col.accessorKey)) {
        if (!state.columnVisibility[colId]) {
          turnedOff.push(colId);
          delete columnSizing[colId];
        } else if (state.columnVisibility[colId] && !storageColumnSizing[colId]) {
          turnedOn.push(colId);
        }
      }

      turnedOn.forEach((colId, idx) => {
        const prevColumnId = turnedOff[idx];

        if (prevColumnId && storageColumnSizing[prevColumnId] != null) {
          columnSizing[colId] = storageColumnSizing[prevColumnId];
        } else {
          columnSizing[colId] = 150;
        }
      });

      const columnsSum = Object.keys(columnSizing)
        .filter(columnId => visibleColumns.find(col => col.accessorKey === columnId))
        .reduce((sum, colId) => sum + columnSizing[colId], 0);


      const documentWidth = document.documentElement.clientWidth;
      const tableContainerWidth = documentWidth - leftSidebarWidth;
      const diff = tableContainerWidth - columnsSum;
  
      const correctColumnSizing = Object.fromEntries(
        Object.entries(columnSizing).map(([id, size]) => {
          const proportion = columnsSum ? size / columnsSum : 0;
          const newSize = size + diff * proportion;
  
          return [id, newSize > this.columnMinSize ? newSize : this.columnMinSize];
        }),
      );

      const resultColumns = visibleColumns.map(col => {
        return {
          ...col,
          size: correctColumnSizing[col.accessorKey],
        }
      });

      return {
        columnSizing: correctColumnSizing,
        columns: resultColumns,
      };
    }

    const widthForCalculateColumnsWithAutoWidth = document.documentElement.clientWidth - (columnsSum + leftSidebarWidth);
    const resultColumns = visibleColumns.map(col => {
      let size = col.size;

      if (size === 'auto') {
        size = (widthForCalculateColumnsWithAutoWidth / columnsCountWithAutoWidth.length);
      }

      return {
        ...col,
        size,
      };
    });
    const columnSizing = Object.fromEntries(
      resultColumns.map(col => [col.accessorKey || col.id, col.size]),
    );

    return {
      columnSizing,
      columns: resultColumns,
    };
  }

  getOrderedVisibleColumns(columns, state) {
    const visibleColumnsIds = columns.filter(col => state.columnVisibility[col.accessorKey || col.id]);
    const visibleColumnsMap = Object.fromEntries(
      visibleColumnsIds.map(col => [col.accessorKey || col.id, col]),
    );

    const orderedVisibleColumns = state.columnOrder
      .filter(key => visibleColumnsMap[key])
      .map(key => visibleColumnsMap[key]);

    return orderedVisibleColumns;
  }

  updateTable() {
    const tableHeaders = this.tableInstance?.getHeaderGroups()[0].headers;
    const tableRows = this.tableInstance?.getExpandedRowModel().rows;

    this.tableHeaders = tableHeaders;
    this.tableRows = tableRows;
    this.virtualRows = this.virtualizerInstance.getVirtualItems();
  }

  updateVirtualizer() {
    if (this.virtualizerInstance) {
      const count = this.tableInstance?.getExpandedRowModel().rows.length;

      this.virtualizerInstance.setOptions({
        ...this.virtualizerInstance.options,
        count,
      });

      this.virtualizerInstance.measure();
    }
  }

  updateData(newData) {
    if (this.tableInstance) {
      this.tableInstance.setOptions(prev => ({
        ...prev,
        data: newData,
      }));
      this.updateVirtualizer();
    }
  }

  updateGrouping(newGrouping) {
    if (this.tableInstance) {
      this.tableInstance.setOptions(prev => ({
        ...prev,
        state: {
          ...prev.state,
          columnSizing: prev.state.columnSizing,
          grouping: newGrouping,
          expanded: prev.state.expanded,
        },
      }));
      this.updateVirtualizer();
    }
  }

  updateVisibleColumns(newVisibility, config) {
    if (!this.tableInstance) return;

    const prevColumnSizing = this.tableInstance.getState().columnSizing;
    const prevColumnVisibility = this.tableInstance.getState().columnVisibility;
    const newColumnSizing = { ...prevColumnSizing };

    const turnedOff = [];
    const turnedOn = [];

    for (const colId of config.columns.map(col => col.accessorKey)) {
      if (prevColumnVisibility[colId] && !newVisibility[colId]) {
        turnedOff.push(colId);
        delete newColumnSizing[colId];
      } else if (!prevColumnVisibility[colId] && newVisibility[colId]) {
        turnedOn.push(colId);
      }
    }

    turnedOn.forEach((colId, idx) => {
      const prevColumnId = turnedOff[idx];

      if (prevColumnId && prevColumnSizing[prevColumnId] != null) {
        newColumnSizing[colId] = prevColumnSizing[prevColumnId];
      } else {
        newColumnSizing[colId] = 150;
      }
    });

    this.controllerColumnSizing = newColumnSizing;
    const newColumns = this.getOrderedVisibleColumns(config.columns, {
      ...config.state,
      columnVisibility: newVisibility,
    })
    this.tableInstance.setOptions(prev => ({
      ...prev,
      columns: newColumns,
      state: {
        ...prev.state,
        columnVisibility: newVisibility,
        columnSizing: newColumnSizing,
        expanded: prev.state.expanded,
      },
    }));

    this.updateVirtualizer();
  }

  onResize(info) {
    if (info?.isResizingColumn) {
      const newSize = info.startSize + (info.deltaOffset || 0);

      this.onAdaptiveResizing(info.isResizingColumn, newSize);
    } else {
      this.tableInstance.setOptions(prev => ({
        ...prev,
        state: {
          ...prev.state,
          grouping: prev.state.grouping,
          expanded: prev.state.expanded,
          columnSizing: this.controllerColumnSizing,
        },
      }));
    }
  }

  onAdaptiveResizing(resizeColumnId, newSize) {
    const headers = this.tableInstance.getHeaderGroups()[0].headers;
    const resizeColumnIndex = headers.findIndex(c => c.id === resizeColumnId);
    const afterCols = headers.slice(resizeColumnIndex + 1);

    const documentWidth = document.documentElement.clientWidth;
    const leftSidebarWidth = Store.getters.leftSidebarWidth;

    let newColumnSizing = {
      ...this.controllerColumnSizing,
      [resizeColumnId]: newSize,
    };

    if (newSize < this.columnMinSize) {
      this.controllerColumnSizing = { ...this.controllerColumnSizing };

      return;
    }

    const columnsSum = Object.values(newColumnSizing)
      .reduce((acc, val) => acc + val, 0);
    const sum = columnsSum + leftSidebarWidth;
    const nextColumn = afterCols[0];

    if (sum < documentWidth) {
      const extra = documentWidth - sum;

      if (nextColumn) {
        newColumnSizing = {
          ...newColumnSizing,
          [nextColumn.id]: newColumnSizing[nextColumn.id] + extra,
        };
      }

      this.controllerColumnSizing = newColumnSizing;

      return;
    }

    if (sum >= documentWidth) {
      const extra = sum - documentWidth;

      if (nextColumn && newColumnSizing[nextColumn.id] > this.columnMinSize) {
        newColumnSizing = {
          ...newColumnSizing,
          [nextColumn.id]: newColumnSizing[nextColumn.id] - extra,
        };

        this.controllerColumnSizing = newColumnSizing;

        return;
      }

      this.controllerColumnSizing = { ...this.controllerColumnSizing };
    }
  }

  updateColumnSizesAfterResizeWindow() {
    const documentWidth = document.documentElement.clientWidth;
    const leftSidebarWidth = Store.getters.leftSidebarWidth;
    const columnsSum = Object.values(this.controllerColumnSizing)
      .reduce((acc, val) => acc + val, 0);

    const tableContainerWidth = documentWidth - leftSidebarWidth;
    const diff = tableContainerWidth - columnsSum;

    this.controllerColumnSizing = Object.fromEntries(
      Object.entries(this.controllerColumnSizing).map(([id, size]) => {
        const proportion = columnsSum ? size / columnsSum : 0;
        const newSize = size + diff * proportion;

        return [id, newSize > this.columnMinSize ? newSize : this.columnMinSize];
      }),
    );

    this.tableInstance.setOptions(prev => ({
      ...prev,
      state: {
        ...prev.state,
        columnSizing: this.controllerColumnSizing,
        expanded: prev.state.expanded,
      },
    }));
  }

  setSorting(updaterOrValue) {
    // const prevSorting = this.tableInstance?.options?.state?.sorting ?? [];
    const prevSorting = this.tableInstance?.getState()?.sorting ?? [];

    const newSorting = typeof updaterOrValue === 'function'
      ? updaterOrValue(prevSorting)
      : updaterOrValue;

    this.tableInstance?.setOptions(prev => ({
      ...prev,
      state: {
        ...prev.state,
        sorting: newSorting,
      },
    }));

    this.updateVirtualizer();
  }

  setExpanded(updaterOrValue) {
    if (this.isUpdating) {
      this.isUpdating = false;

      return;
    }

    // const prevExpanded = this.tableInstance?.options?.state?.expanded ?? {};
    const prevExpanded = this.tableInstance?.getState()?.expanded ?? {};

    const newExpanded = typeof updaterOrValue === 'function'
      ? updaterOrValue(prevExpanded)
      : updaterOrValue;

    this.tableInstance?.setOptions(prev => ({
      ...prev,
      state: {
        ...prev.state,
        expanded: newExpanded,
      },
    }));

    this.updateVirtualizer();
  }

  toggleAllExpanded(flag) {
    if (this.tableInstance) {
      this.tableInstance.toggleAllRowsExpanded(flag);
      this.updateVirtualizer();
    }
  }

  toggleTopLevelExpanded(expand) {
    if (!this.tableInstance) return;

    const table = this.tableInstance;
    const currentExpanded = table.getState().expanded ?? {};
    const nextExpanded = { ...currentExpanded };

    table.getRowModel().rows.forEach(row => {
      if (row.depth !== 0 || !row.getCanExpand()) return;

      if (expand) {
        nextExpanded[row.id] = true;
      } else {
        delete nextExpanded[row.id];
      }
    });

    table.setExpanded(nextExpanded);
    this.updateVirtualizer?.();
  }

  getRowPositionStyle(virtualRow) {
    const rows = this.tableInstance.getExpandedRowModel().rows;

    const currentRow = rows[virtualRow.index];

    if (!currentRow) return;

    const isTopLevelGroup = currentRow.depth === 0;
    const prevRow = rows[virtualRow.index - 1];
    const isNewTopLevelGroup = prevRow && currentRow.groupingValue !== prevRow.groupingValue && isTopLevelGroup;

    let result = {
      height: `${virtualRow.size}px`,
      transform: `translateY(${virtualRow.start}px)`,
    };

    if (isNewTopLevelGroup) {
      result = {
        ...result,
        ...TABLE_STYLES.ROWS.paddingTop,
      }
    }

    return result;
  }

  getRowVisualStyle(virtualRow) {
    const rows = this.tableInstance.getExpandedRowModel().rows;

    const currentRow = rows[virtualRow.index];

    if (!currentRow) return;

    const isGroupHeader = currentRow.getIsGrouped();
    const isTopLevelGroup = currentRow.depth === 0;
    const currentDepth = currentRow.depth;
    const nextRow = rows[virtualRow.index + 1];
    const grouping = this.tableInstance?.getState().grouping;
    const isLastVisibleRowInGroup = !nextRow || nextRow.depth === 0;

    let result = {
      ...TABLE_STYLES.ROWS.sideBorders,
      ...TABLE_STYLES.ROWS.borderBottom,
    };

    if (grouping?.length) {
      if (!isGroupHeader && isLastVisibleRowInGroup) {
        result = { ...result, ...TABLE_STYLES.ROWS.bottomRadius };
      }

      if (isGroupHeader && !currentRow.getIsExpanded() && isLastVisibleRowInGroup) {
        result = { ...result, ...TABLE_STYLES.ROWS.bottomRadius };
      }

    } else {
      if (virtualRow.index === 0) {
        result = { ...result, ...TABLE_STYLES.ROWS.topRadius, ...TABLE_STYLES.ROWS.borderTop };
      }

      if (virtualRow.index === rows.length - 1) {
        result = { ...result, ...TABLE_STYLES.ROWS.bottomRadius };
      }

      if (rows.length === 1) {
        result = { ...result, ...TABLE_STYLES.ROWS.allBorderRadius };
      }
    }

    if (isGroupHeader && isTopLevelGroup) {
      result = { ...result, ...TABLE_STYLES.ROWS.topRadius, ...TABLE_STYLES.ROWS.borderTop };
    }

    // if (isGroupHeader && isNewTopLevelGroup && isTopLevelGroup) {
    //   result = { ...result, ...TABLE_STYLES.ROWS.marginTop };
    // }

    if (isGroupHeader && !currentRow.getIsExpanded() && isTopLevelGroup) {
      result = { ...result, ...TABLE_STYLES.ROWS.allBorderRadius };
    }

    // if (isGroupHeader && currentDepth === 1) {
    //   result = { ...result, ...TABLE_STYLES.ROWS.backgroundColor };
    // }

    if (isGroupHeader) {
      const backgroundColor = TABLE_STYLES.ROWS.backgroundColor[currentDepth] ?? TABLE_STYLES.ROWS.backgroundColor[0];

      result = { ...result, backgroundColor };
    }

    return result;
  }

  getGroupedCellStyle(settings) {
    const {
      row, cell, columnSizes, config,
    } = settings;

    if (!row.getIsGrouped()) return null;

    const groupCellId = cell.getIsGrouped() ? cell.column.id : '';
    // const aggregatedColumns = row.depth === 0 ? config.aggregatedColumns : [];
    const aggregatedColumns = config.aggregatedColumns;

    const visibleColumns = config.columns.filter(col => config.state.columnVisibility[col.accessorKey]);
    const visibleAggregatedColumns = aggregatedColumns.filter(id => config.state.columnVisibility[id]);
    const visibleColumnsMap = Object.fromEntries(
      visibleColumns.map(col => [col.accessorKey, col]),
    );

    const orderedVisibleColumns = config.state.columnOrder
      .filter(key => visibleColumnsMap[key])
      .map(key => visibleColumnsMap[key]);

    const hiddenColumnsIds = orderedVisibleColumns
      .filter(col => col.accessorKey !== groupCellId && !visibleAggregatedColumns.includes(col.accessorKey))
      .map(col => col.accessorKey);

    const columnsForCalculateGroupCellWidth = visibleAggregatedColumns.length
      ? orderedVisibleColumns.slice(0, orderedVisibleColumns.findIndex(col => col.accessorKey === visibleAggregatedColumns[0]))
      : orderedVisibleColumns;

    const groupCellWidth = columnsForCalculateGroupCellWidth.reduce((sum, col) => sum + (parseFloat(columnSizes[col.accessorKey]) || 0), 0);

    if (hiddenColumnsIds.includes(cell.column.id)) {
      return { display: 'none' };
    }

    if (cell.getIsGrouped()) {
      return { width: `${groupCellWidth}px` };
    }

    return null;
  }

  createTableTotalRow(config, columnSizes, currentRoute) {
    if (!config.aggregatedColumns?.length) {
      return [];
    }

    const visibleColumns = this.getOrderedVisibleColumns(config.columns, config.state);
    const result = visibleColumns.map(column => {
      const value = this.getTotalValueByColumnId(column.accessorKey);
      const formattedValue = reportHelpers.getFormattedValueByReportRoute(value || 0, currentRoute);

      return {
        id: column.accessorKey,
        size: columnSizes[column.accessorKey],
        value: formattedValue,
        show: config.aggregatedColumns.includes(column.accessorKey),
        isNegativeValue: value < 0,
      }
    })

    return result;
  }

  getTotalValueByColumnId(columnId) {
    if (this.tableInstance?.getState()?.grouping?.length) {
      const groupsRows = this.tableInstance?.getGroupedRowModel().rows;
      const firstLevelGroups = groupsRows.filter(g => g.depth === 0);

      const totalValue = firstLevelGroups.reduce((sum, row) => {
        const value = row.getValue(columnId);

        return sum + (isNaN(+value) ? 0 : +value);
      }, 0);

      // return Math.ceil(totalValue * 100) / 100;
      return (totalValue * 100) / 100;
    }

    const rows = this.tableInstance?.getExpandedRowModel().rows;
    const totalValue = rows.reduce((sum, row) => {
      const value = row.getValue(columnId);

      return sum + +value;
    }, 0);

    // return Math.ceil(totalValue * 100) / 100;
    return (totalValue * 100) / 100;
  }
}
